import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { useQuery, resolved } from '@hapana/shared/web/services/gqty'
import { gql } from 'graphql-request'
import { gqlMutate } from '@hapana/shared/web/services/requests.service'
import { useChatStream } from '@hapana/shared/web/hooks/use-chat-stream.hook'
import { db } from '../../config/firebase.config'

import { useTranslation } from 'react-i18next'
import {
  ActivePanel,
  PendingDocsArticle,
  PlanTier,
  ScopeLevel,
  SupportAssistantTab,
  SupportContextValue,
  SupportMessage,
  UserRole,
} from './support-assistant.types'
import { getPageDisplayName } from './support-assistant-config'
import useSiteStore from '../../stores/site.store'
import useCorporateStore from '../../stores/corporate.store'
import { useMe } from '../../hooks/me.hooks'
import { useGetSiteById } from '../../hooks/gqty/sites.hooks'

// LocalStorage key for sidebar state
const SIDEBAR_STATE_KEY = 'support-assistant-sidebar-open'

// Type for the me query response
interface MeData {
  me: {
    id: string
    email: string
    firstName: string
    lastName: string
    phone?: string
    staffUserPermissions?: string[]
  }
}

// Types for support chat GraphQL responses
interface SupportChatMessage {
  supportChatMessageId: string
  role: string
  text: string
  html?: string
  createdAt: string
}

export const SupportAssistantContext = createContext<SupportContextValue | null>(null)

export const SupportAssistantProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const { t } = useTranslation('core')

  // Initialize isOpen from localStorage
  const [isOpen, setIsOpenState] = useState(() => {
    try {
      const stored = localStorage.getItem(SIDEBAR_STATE_KEY)
      return stored === 'true'
    } catch (error) {
      console.warn('Failed to read sidebar state from localStorage:', error)
      return false
    }
  })

  const [chatId, setChatId] = useState<string | null>(null)
  const [messages, setMessages] = useState<SupportMessage[]>([])
  const [isStreaming, setIsStreaming] = useState(false)
  const [isLoadingChat, setIsLoadingChat] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Top-level tab navigation
  const [activeTab, setActiveTab] = useState<SupportAssistantTab>('chats')
  // Navigation state (within Assistant tab)
  const [currentView, setCurrentView] = useState<'chat' | 'history' | 'ticket'>('chat')
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null)
  const [selectedChatTitle, setSelectedChatTitle] = useState<string | null>(null)
  const [prefillDescription, setPrefillDescription] = useState<string | null>(null)
  const [prefillIssueType, setPrefillIssueType] = useState<string | null>(null)
  // Docs tab article navigation (from related article links or URL query params)
  const [pendingDocsArticle, setPendingDocsArticle] = useState<PendingDocsArticle | null>(null)
  // Which content is shown in the right panel (assistant chat vs onboarding checklist)
  const [activePanel, setActivePanel] = useState<ActivePanel>('assistant')

  const [activeStreamId, setActiveStreamId] = useState<string | null>(null)

  const { thinkingStep, thoughtText } = useChatStream({
    db,
    streamId: activeStreamId,
    enabled: !!activeStreamId,
    onTextUpdate: (text) => {
      setMessages((prev) => {
        const lastMsg = prev[prev.length - 1]
        if (lastMsg?.role === 'assistant' && lastMsg.type === 'streaming') {
          return [...prev.slice(0, -1), { ...lastMsg, content: text }]
        }
        return [
          ...prev,
          {
            id: `stream-${activeStreamId}`,
            chatId: chatId || 'pending',
            role: 'assistant' as const,
            content: text,
            type: 'streaming' as const,
            createdAt: new Date().toISOString(),
          },
        ]
      })
    },
    onComplete: (finalText, finalHtml, metadata) => {
      setMessages((prev) => {
        const lastMsg = prev[prev.length - 1]
        if (lastMsg?.role === 'assistant' && lastMsg.type === 'streaming') {
          return [
            ...prev.slice(0, -1),
            { ...lastMsg, content: finalText, html: finalHtml, type: 'final' as const, metadata },
          ]
        }
        return prev
      })
      setActiveStreamId(null)
      setIsStreaming(false)
    },
    onError: (error) => {
      console.error('[SupportAssistant] Stream error:', error)
      setActiveStreamId(null)
      setIsStreaming(false)
      setError(t('supportAssistant.errors.failedToGetResponse'))
    },
  })

  // URL query params for shareable state
  const [searchParams, setSearchParams] = useSearchParams()

  // Wrapper for setIsOpen that persists to localStorage
  const setIsOpen = useCallback((open: boolean) => {
    setIsOpenState(open)
    try {
      localStorage.setItem(SIDEBAR_STATE_KEY, String(open))
    } catch (error) {
      console.warn('Failed to save sidebar state to localStorage:', error)
    }
  }, [])

  // Auto-detect scope context from stores (brand vs location)
  const location = useLocation()
  const { currentSite, getCurrentSiteName, getCurrentSiteId } = useSiteStore()
  const { corporate, brandName } = useCorporateStore()

  const { data: siteData } = useGetSiteById(getCurrentSiteId() || undefined)
  const siteTimezone = siteData?.timezone || undefined

  // Extract IDs and names
  const locationId = getCurrentSiteId() || null
  const locationName = getCurrentSiteName() || null
  const brandId = corporate?.corporateBySubdomain?.corporateId || null
  const extractedBrandName = brandName || null

  // Derive scope level and name
  const scopeLevel: ScopeLevel = currentSite ? 'location' : 'brand'
  const scopeName =
    scopeLevel === 'location' ? locationName || 'Location' : extractedBrandName || 'Brand'

  // Get page context
  const pathname = location.pathname
  const pageName = getPageDisplayName(pathname)

  // Get user role and ID from me query
  const { data: meData } = useMe() as { data: MeData | undefined }
  // The core app is staff-facing (site staff, trainers, front-office).
  // 'staff' is correct for all core app users — 'admin' is only for the admin app.
  // Granular roles (brand-admin, site-admin, front-office, trainer) require
  // a `type` field on the me query which is not yet exposed.
  const userRole: UserRole = 'staff'
  const userId = meData?.me?.id || 'unknown'

  // Close sidebar when user is not authenticated (e.g. on login page)
  useEffect(() => {
    if (!meData?.me) {
      setIsOpenState(false)
    }
  }, [meData?.me])

  // Plan tier is not yet exposed in the corporate store or GQty schema.
  // Defaults to 'standard' until subscription data is available in context.
  const planTier: PlanTier = 'standard'

  // Build userInfo from meData
  const userInfo = useMemo(
    () =>
      meData?.me
        ? {
            firstName: meData.me.firstName || 'User',
            lastName: meData.me.lastName || 'Name',
            email: meData.me.email || '',
            userId: meData.me.id,
          }
        : null,
    [meData]
  )

  // GraphQL query for fetching chat history
  const query = useQuery({ suspense: false }) as any

  const loadChat = useCallback(
    async (chatIdToLoad: string, chatTitle?: string) => {
      try {
        setSelectedChatId(chatIdToLoad)
        setSelectedChatTitle(chatTitle || null)
        setChatId(chatIdToLoad)
        setCurrentView('chat')
        setIsLoadingChat(true)
        setMessages([])

        const chatData = await resolved(() => {
          const data = query.supportChatWithMessages({ supportChatId: chatIdToLoad })

          if (data?.messages) {
            data.messages.forEach((msg: any) => {
              void msg.supportChatMessageId
              void msg.role
              void msg.text
              void msg.html
              void msg.createdAt
            })
          }

          return data
        })

        if (chatData?.messages && Array.isArray(chatData.messages)) {
          const supportMessages: SupportMessage[] = chatData.messages
            .filter((msg: SupportChatMessage) => msg?.role)
            .map((msg: SupportChatMessage) => ({
              id: msg.supportChatMessageId,
              chatId: chatIdToLoad,
              role: msg.role.toLowerCase() as 'user' | 'assistant',
              content: msg.text || '',
              html: msg.html,
              type: 'final',
              createdAt: msg.createdAt,
            }))

          setMessages(supportMessages)
        } else {
          setMessages([])
        }
      } catch (error) {
        console.error('Failed to load chat:', error)
        setError(t('supportAssistant.errors.failedToLoadChat'))
      } finally {
        setIsLoadingChat(false)
      }
    },
    [query]
  )

  const backToNewChat = useCallback(() => {
    setSelectedChatId(null)
    setSelectedChatTitle(null)
    setMessages([])
    setChatId(null)
    setCurrentView('chat')
  }, [])

  // Open a specific KB article in the Docs tab
  const openDocsArticle = useCallback(
    (slug: string, title: string) => {
      setPendingDocsArticle({ slug, title })
      setActiveTab('docs')
      setIsOpen(true)
    },
    [setIsOpen]
  )

  // Clear the pending docs article (e.g. when user clicks Back in docs tab)
  const clearPendingDocsArticle = useCallback(() => {
    setPendingDocsArticle(null)
  }, [])

  // Sync URL query params when state changes
  // Read current params from window.location to avoid searchParams in deps (causes infinite loop)
  useEffect(() => {
    const newParams = new URLSearchParams(window.location.search)
    let changed = false

    // Sync 'support' param
    if (isOpen) {
      if (newParams.get('support') !== '1') {
        newParams.set('support', '1')
        changed = true
      }
    } else {
      if (newParams.has('support')) {
        newParams.delete('support')
        newParams.delete('ptab')
        newParams.delete('article')
        changed = true
      }
    }

    // Sync 'panel' param (only when open and on onboarding)
    if (isOpen && activePanel === 'onboarding') {
      if (newParams.get('panel') !== 'onboarding') {
        newParams.set('panel', 'onboarding')
        changed = true
      }
    } else {
      if (newParams.has('panel')) {
        newParams.delete('panel')
        changed = true
      }
    }

    // Sync 'ptab' param (only when open) — using 'ptab' to avoid conflicting with page-level 'tab' params
    if (isOpen) {
      if (newParams.get('ptab') !== activeTab) {
        newParams.set('ptab', activeTab)
        changed = true
      }
    }

    // Sync 'article' param (only when docs tab has a pending article)
    if (isOpen && activeTab === 'docs' && pendingDocsArticle) {
      if (newParams.get('article') !== pendingDocsArticle.slug) {
        newParams.set('article', pendingDocsArticle.slug)
        changed = true
      }
    } else {
      if (newParams.has('article')) {
        newParams.delete('article')
        changed = true
      }
    }

    if (changed) {
      setSearchParams(newParams, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- searchParams excluded to prevent infinite loop; we read from window.location instead
  }, [isOpen, activeTab, activePanel, pendingDocsArticle, setSearchParams])

  // Restore state from URL query params on mount
  useEffect(() => {
    const supportParam = searchParams.get('support')
    const tabParam = searchParams.get('ptab') as SupportAssistantTab | null
    const articleParam = searchParams.get('article')
    const panelParam = searchParams.get('panel')

    if (supportParam === '1') {
      setIsOpenState(true)
      try {
        localStorage.setItem(SIDEBAR_STATE_KEY, 'true')
      } catch {
        // ignore
      }

      if (panelParam === 'onboarding') {
        setActivePanel('onboarding')
      }

      if (tabParam && ['chats', 'support', 'docs', 'activity'].includes(tabParam)) {
        setActiveTab(tabParam)
      }

      if (tabParam === 'docs' && articleParam) {
        // Title will be fetched by DocsTab when it loads the article
        setPendingDocsArticle({ slug: articleParam, title: articleParam })
      }
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const startSupportChatStreamGql = gql`
    mutation StartSupportChatStream($input: StartSupportChatInput!) {
      startSupportChatStream(input: $input) {
        chatId
        streamId
        userMessageId
      }
    }
  `

  const streamSupportMessageGql = gql`
    mutation StreamSupportMessage($input: StreamSupportMessageInput!) {
      streamSupportMessage(input: $input) {
        messageId
        streamId
        supportChatId
        success
      }
    }
  `

  const addTicketMessage = useCallback(
    (subject: string, description: string) => {
      // Create a system message for the ticket submission
      const ticketMsg: SupportMessage = {
        id: `ticket-${Date.now()}`,
        chatId: chatId || 'pending',
        role: 'system',
        content: `Support Ticket Submitted\n\nSubject: ${subject}\n\nDescription: ${description}\n\nOur support team will get back to you soon via email.`,
        type: 'final',
        createdAt: new Date().toISOString(),
        isSupportTicket: true,
      }
      setMessages((prev) => [...prev, ticketMsg])
    },
    [chatId]
  )

  const sendMessage = useCallback(
    async (text: string, displayText?: string) => {
      if (!text.trim()) return
      if (!userId || userId === 'unknown') {
        console.warn('⚠️ User ID not available yet')
        return
      }

      setError(null)

      const userMsg: SupportMessage = {
        id: `user-${Date.now()}`,
        chatId: chatId || 'pending',
        role: 'user',
        content: (displayText ?? text).trim(),
        type: 'final',
        createdAt: new Date().toISOString(),
      }
      setMessages((prev) => [...prev, userMsg])
      setIsStreaming(true)

      try {
        if (!chatId) {
          const response = await gqlMutate({
            mutation: startSupportChatStreamGql,
            variables: {
              input: {
                userId,
                initialMessage: text,
                source: 'UI',
                planTier,
                userRole,
                scopeLevel,
                scopeName,
                pathname,
                pageName,
                pageContextMeta: { pathname, pageName },
                locationId: locationId ?? undefined,
                locationName: locationName ?? undefined,
                timezone: siteTimezone,
              },
            },
            options: { isV1: false },
          })

          const result = response?.startSupportChatStream
          if (result?.chatId) {
            setChatId(result.chatId)
            setMessages((prev) => prev.map((msg) => ({ ...msg, chatId: result.chatId })))
          }
          if (result?.streamId) {
            setActiveStreamId(result.streamId)
          }
        } else {
          const streamId = `stream-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
          setActiveStreamId(streamId)

          await gqlMutate({
            mutation: streamSupportMessageGql,
            variables: {
              input: {
                supportChatId: chatId,
                text,
                streamId,
                timezone: siteTimezone,
                pageContextMeta: { pathname, pageName },
                locationId: locationId ?? undefined,
                locationName: locationName ?? undefined,
              },
            },
            options: { isV1: false },
          })
        }
      } catch (err) {
        console.error('Error sending message:', err)
        setActiveStreamId(null)
        setIsStreaming(false)
        setError(t('supportAssistant.errors.failedToSendMessage'))
      }
    },
    [
      userId,
      chatId,
      planTier,
      userRole,
      scopeLevel,
      scopeName,
      pathname,
      pageName,
      locationId,
      locationName,
      siteTimezone,
    ]
  )

  const value = useMemo<SupportContextValue>(
    () => ({
      isOpen,
      setIsOpen,
      chatId,
      messages,
      isStreaming,
      thinkingStep,
      thoughtText,
      isLoadingChat,
      sendMessage,
      addTicketMessage,
      planTier,
      userRole,
      userInfo,
      scopeLevel,
      scopeName,
      brandId,
      brandName: extractedBrandName,
      locationId,
      locationName,
      pathname,
      pageName,
      error,
      activeTab,
      setActiveTab,
      currentView,
      setCurrentView,
      selectedChatId,
      selectedChatTitle,
      loadChat,
      backToNewChat,
      prefillDescription,
      setPrefillDescription,
      prefillIssueType,
      setPrefillIssueType,
      pendingDocsArticle,
      openDocsArticle,
      clearPendingDocsArticle,
      activePanel,
      setActivePanel,
    }),
    [
      chatId,
      isOpen,
      isStreaming,
      thinkingStep,
      thoughtText,
      isLoadingChat,
      messages,
      planTier,
      sendMessage,
      addTicketMessage,
      userRole,
      userInfo,
      scopeLevel,
      scopeName,
      brandId,
      extractedBrandName,
      locationId,
      locationName,
      pathname,
      pageName,
      error,
      activeTab,
      currentView,
      selectedChatId,
      selectedChatTitle,
      loadChat,
      backToNewChat,
      prefillDescription,
      prefillIssueType,
      pendingDocsArticle,
      openDocsArticle,
      clearPendingDocsArticle,
      activePanel,
    ]
  )

  return (
    <SupportAssistantContext.Provider value={value}>{children}</SupportAssistantContext.Provider>
  )
}
